import { create } from 'zustand';
import type {
  DiagramPage,
  DiagramType,
  Node,
  Edge,
  NodeGroup,
  Layer,
  Flow,
  Graph,
  SequenceParticipant,
  SequenceMessage,
  SequenceFragment,
  SequenceParticipantGroup,
  CatalogComponent,
} from '../types';
import { useDiagramStore } from './diagramStore';

const MAX_HISTORY = 50;

export interface DiagramSnapshot {
  diagramType: DiagramType;
  nodes: Node[];
  edges: Edge[];
  groups: NodeGroup[];
  participants: SequenceParticipant[];
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  participantGroups: SequenceParticipantGroup[];
  layers: Layer[];
  flows: Flow[];
  pages: DiagramPage[];
  activePage: string;
  graphs: Record<string, Graph>;
  activeGraphId: string;
  viewport: { x: number; y: number; scale: number };
  groupNavStack: Array<{ groupId: string; groupLabel: string; returnViewport: { x: number; y: number; scale: number } }>;
  graphNavStack: Array<{ graphId: string; graphLabel: string; returnViewport: { x: number; y: number; scale: number } }>;
  catalog: CatalogComponent[];
}

interface UndoStore {
  undoStack: DiagramSnapshot[];
  redoStack: DiagramSnapshot[];
  pushSnapshot: (snapshot: DiagramSnapshot) => void;
  undo: () => DiagramSnapshot | null;
  redo: () => DiagramSnapshot | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clear: () => void;
}

function cloneSnapshot<T>(value: T): T {
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
}

export const useUndoStore = create<UndoStore>((set, get) => ({
  undoStack: [],
  redoStack: [],

  pushSnapshot: (snapshot: DiagramSnapshot) => {
    set(state => ({
      undoStack: [
        ...state.undoStack.slice(-MAX_HISTORY + 1),
        cloneSnapshot(snapshot),
      ],
      redoStack: [],
    }));
  },

  undo: () => {
    const { undoStack } = get();
    if (undoStack.length === 0) return null;
    const snapshot = undoStack[undoStack.length - 1];
    set(state => ({
      undoStack: state.undoStack.slice(0, -1),
    }));
    return snapshot;
  },

  redo: () => {
    const { redoStack } = get();
    if (redoStack.length === 0) return null;
    const snapshot = redoStack[redoStack.length - 1];
    set(state => ({
      redoStack: state.redoStack.slice(0, -1),
    }));
    return snapshot;
  },

  canUndo: () => get().undoStack.length > 0,
  canRedo: () => get().redoStack.length > 0,

  clear: () => set({ undoStack: [], redoStack: [] }),
}));

export function captureSnapshot(): DiagramSnapshot {
  const st = useDiagramStore.getState();
  return cloneSnapshot({
    diagramType: st.diagramType,
    nodes: st.nodes,
    edges: st.edges,
    groups: st.groups,
    participants: st.participants,
    messages: st.messages,
    fragments: st.fragments,
    participantGroups: st.participantGroups,
    layers: st.layers,
    flows: st.flows,
    pages: st.pages,
    activePage: st.activePage,
    graphs: st.graphs,
    activeGraphId: st.activeGraphId,
    viewport: st.viewport,
    groupNavStack: st.groupNavStack,
    graphNavStack: st.graphNavStack,
    catalog: st.catalog,
  });
}

export function restoreSnapshot(snapshot: DiagramSnapshot): void {
  useDiagramStore.getState().loadDiagram({
    diagramType: snapshot.diagramType,
    nodes: snapshot.nodes,
    edges: snapshot.edges,
    groups: snapshot.groups,
    participants: snapshot.participants,
    messages: snapshot.messages,
    fragments: snapshot.fragments,
    participantGroups: snapshot.participantGroups,
    layers: snapshot.layers,
    viewport: snapshot.viewport,
    graphs: snapshot.graphs,
    activeGraphId: snapshot.activeGraphId,
    pages: snapshot.pages,
    activePage: snapshot.activePage,
    catalog: snapshot.catalog,
  });
  useDiagramStore.setState({
    flows: snapshot.flows,
    groupNavStack: snapshot.groupNavStack,
    graphNavStack: snapshot.graphNavStack,
  });
}

export const pushToRedo = (snapshot: DiagramSnapshot) => {
  useUndoStore.setState(state => ({
    redoStack: [...state.redoStack, cloneSnapshot(snapshot)],
  }));
};

export function snapshotFromStore() {
  useUndoStore.getState().pushSnapshot(captureSnapshot());
}
